import clsx from 'clsx'
import type { KeyboardEventHandler, ReactNode, RefObject } from 'react'

import { XClose } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import TruncatedText from '@/components/truncated-text/TruncatedText'

type PanelTabProps = {
  label: string
  id: string
  /* Id of the element this tab shows, for aria-controls. */
  controlsId: string
  selected: boolean
  onSelect: () => void
  /* Focused by the tab list when an arrow key lands on this tab. */
  tabRef?: RefObject<HTMLButtonElement>
  onKeyDown?: KeyboardEventHandler<HTMLButtonElement>
  /* Rendered before the label. */
  icon?: ReactNode
  /* Omitted for a tab the panel always offers. */
  onClose?: () => void
  /* Accessible name for the close control; defaults to the label. */
  closeLabel?: string
  size?: 'sm' | 'md'
  className?: string
}

/** A selectable panel tab with an optional independent close control. */
export default function PanelTab({
  label,
  id,
  controlsId,
  selected,
  onSelect,
  tabRef,
  onKeyDown,
  icon,
  onClose,
  closeLabel,
  size = 'md',
  className,
}: PanelTabProps) {
  return (
    // Presentational, because the tab and its close control are two buttons and neither can nest in the other.
    <div
      role="presentation"
      data-ds-component="PanelTab"
      className={clsx(
        // Every tab sits a pixel low so its bottom edge lands on the row's border rather than stacking a second line above it.
        '-mb-px flex min-w-0 items-center gap-px rounded-t-lg border border-border-strong',
        // Horizontal padding belongs to the tab's own button, so the whole tab selects it. The close control
        // insets its own icon, so the room beside it is the remainder rather than a full gap.
        size === 'sm' ? 'h-8' : 'h-9',
        onClose && 'pr-0.5',
        selected
          ? // The open tab paints that edge in the panel's own background instead, joining the tab to what it shows.
            'border-b-surface-content bg-surface-content'
          : 'bg-surface-subtle hover:bg-surface-hover-strong',
        className
      )}
    >
      <button
        role="tab"
        type="button"
        id={id}
        ref={tabRef}
        aria-controls={controlsId}
        aria-selected={selected}
        // The tab list is one stop in the page's tab order: Tab reaches the open tab, the arrow keys move between them.
        tabIndex={selected ? 0 : -1}
        onClick={onSelect}
        onKeyDown={onKeyDown}
        className={clsx('flex h-full min-w-0 items-center gap-1 pl-2 text-xs leading-4', !onClose && 'pr-2.5', selected && 'cursor-default')}
      >
        {icon}
        <TruncatedText
          placement="bottom-start"
          className={selected ? 'font-semibold text-text-primary' : 'font-medium text-true-gray-700'}
          // A tap on the tooltip would otherwise swallow the one selecting the tab.
          disableOnTouch
        >
          {label}
        </TruncatedText>
      </button>
      {onClose && (
        <IconButton
          variant="plain"
          color="neutral"
          size="sm"
          icon={<XClose />}
          aria-label={`Close ${closeLabel ?? label}`}
          onClick={onClose}
          // Keeps its size when the title truncates, so the X sits the same distance from the tab's edge either way.
          wrapperClassName="shrink-0"
          // Travels with its tab, so the tab list stays one stop whichever tab is open.
          tabIndex={selected ? 0 : -1}
          // Overrides the icon button's hover colour so the X reads against a surface-hover-strong tab, not because the fills match.
          className={selected ? undefined : 'hover:!bg-surface-hover-stronger'}
        />
      )}
    </div>
  )
}
